/**
 * MultiGroupPicker — Feb 2026 multi-group evolution.
 *
 * Manages a contact's set of group memberships as ordered chips. Wraps
 * the existing single-pick GroupPicker for "+ add another group" so the
 * collision dialog, dedupe, and typeahead all come for free.
 *
 * Props
 *   value: List<string>           — current group_ids (order matters; [0] = primary)
 *   onChange(group_ids): void
 *   defaultType: 'family'|'company'|'organization'  — type used when seeding the +Add picker
 *   defaultName: string           — name suggested in the +Add picker
 *
 * data-testids:
 *   multi-group-picker
 *   multi-group-chip-{group_id}
 *   multi-group-chip-remove-{group_id}
 *   multi-group-promote-{group_id}   — promote chip to primary (move to index 0)
 *   multi-group-add-btn
 *   multi-group-add-typeahead
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import GroupPicker from '@/components/GroupPicker';
import { Button } from '@/components/ui/button';
import {
  Building2, Users, Globe, X, Star, Plus,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const TYPE_META = {
  company: {
    icon: Building2,
    label: 'Company',
    // Explicit hover overrides so the chip never inverts to dark on hover —
    // the star/name must stay readable since the star is how users
    // promote a non-primary group to primary.
    tone: 'bg-sky-50 text-sky-900 border-sky-200 hover:bg-sky-100 hover:text-sky-900',
  },
  family: {
    icon: Users,
    label: 'Family',
    tone: 'bg-violet-50 text-violet-900 border-violet-200 hover:bg-violet-100 hover:text-violet-900',
  },
  organization: {
    icon: Globe,
    label: 'Organization',
    tone: 'bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100 hover:text-amber-900',
  },
};

export default function MultiGroupPicker({ value, onChange, defaultType = 'family', defaultName = '' }) {
  const ids = Array.isArray(value) ? value : [];
  const [resolved, setResolved] = useState({});  // gid → group dict
  const [addingOpen, setAddingOpen] = useState(false);
  const [pendingNewId, setPendingNewId] = useState(null);

  // Resolve any unknown gids — single batched fetch so chips render the
  // group name and type without N round-trips.
  useEffect(() => {
    const missing = ids.filter(gid => gid && !resolved[gid]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/contact-groups`, { headers: authHeaders() });
        if (cancelled) return;
        const map = {};
        for (const g of res.data?.groups || []) {
          map[g.id] = g;
        }
        setResolved(prev => ({ ...prev, ...map }));
      } catch {
        // Best-effort; chips will render with raw ids.
      }
    })();
    return () => { cancelled = true; };
  }, [ids, resolved]);

  const remove = useCallback((gid) => {
    onChange(ids.filter(g => g !== gid));
  }, [ids, onChange]);

  const promote = useCallback((gid) => {
    if (!ids.includes(gid) || ids[0] === gid) return;
    onChange([gid, ...ids.filter(g => g !== gid)]);
  }, [ids, onChange]);

  const handlePickerChange = useCallback((newGid) => {
    setPendingNewId(newGid);
    if (newGid && !ids.includes(newGid)) {
      onChange([...ids, newGid]);
    }
    setAddingOpen(false);
    // Reset internal picker state next render.
    setTimeout(() => setPendingNewId(null), 50);
  }, [ids, onChange]);

  return (
    <div className="space-y-2" data-testid="multi-group-picker">
      {/* Chips for current memberships. The first chip is the PRIMARY
          (mirrors onto contact.company when company-type). Star icon
          marks it; click any non-primary chip's star to promote. */}
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {ids.map((gid, idx) => {
            const g = resolved[gid];
            const type = g?.type || 'family';
            const meta = TYPE_META[type] || TYPE_META.family;
            const Icon = meta.icon;
            const isPrimary = idx === 0;
            return (
              <span
                key={gid}
                className={`inline-flex items-center gap-1 pl-1.5 pr-0.5 py-0.5 rounded-md border text-xs transition-colors ${meta.tone}`}
                data-testid={`multi-group-chip-${gid}`}
              >
                <Icon className="w-3 h-3 flex-shrink-0" />
                <span className="text-[11px]">{g?.name || gid.slice(0, 8)}</span>
                {isPrimary ? (
                  <Star
                    className="w-3 h-3 ml-0.5 fill-current opacity-80"
                    title="Primary group — mirrors onto contact.company when company-type"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => promote(gid)}
                    className="ml-0.5 p-0.5 rounded hover:bg-black/10"
                    title="Promote to primary"
                    data-testid={`multi-group-promote-${gid}`}
                  >
                    <Star className="w-3 h-3 opacity-60" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(gid)}
                  className="p-0.5 rounded hover:bg-black/10"
                  title="Remove from this group"
                  data-testid={`multi-group-chip-remove-${gid}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* +Add another group — opens an inline GroupPicker. We mount/unmount
          to reset its internal state every time. */}
      {!addingOpen && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setAddingOpen(true)}
          className="h-7 text-xs"
          data-testid="multi-group-add-btn"
        >
          <Plus className="w-3 h-3 mr-1" />
          {ids.length === 0 ? 'Add a group' : 'Add another group'}
        </Button>
      )}
      {addingOpen && (
        <div className="rounded-md border border-stone-200 bg-stone-50 p-2 space-y-1.5">
          <GroupPicker
            value={pendingNewId}
            onChange={handlePickerChange}
            defaultType={defaultType}
            defaultName={defaultName}
          />
          <div className="flex items-center justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setAddingOpen(false)}
              className="h-6 text-[11px]"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {ids.length === 0 && !addingOpen && (
        <p className="text-[11px] text-muted-foreground">
          No groups yet — defaults will seed on save based on company / last name.
        </p>
      )}
    </div>
  );
}
